import React, { useState } from 'react';
import { Loader } from 'lucide-react';
import { useLazyQuery, gql } from '@apollo/client';
import { toast } from '@/components/ui/toast';

const REQUEST_TWO_FACTOR_BY_TOKEN = gql`
  query RequestTwoFactorByToken($token: String!) {
    requestTwoFactorByToken(token: $token)
  }
`;

const VALIDATE_TWO_FACTOR_BY_TOKEN = gql`
  query ValidateTwoFactorByToken($token: String!, $code: String!) {
    validateTwoFactorByToken(token: $token, code: $code)
  }
`;

interface TwoFactorLoginProps {
  token?: string;
  setTwofactorPass: (passed: boolean, newToken: string) => void;
}

export const TwoFactorLogin: React.FC<TwoFactorLoginProps> = ({ token, setTwofactorPass }) => {
  const [twoFactorBoxValue, setTwoFactorBoxValue] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  const [codeRequested, setCodeRequested] = useState<boolean>(false);

  const [requestTwoFactorByToken] = useLazyQuery<{ requestTwoFactorByToken: boolean }>(REQUEST_TWO_FACTOR_BY_TOKEN);
  const [validateTwoFactorByToken] = useLazyQuery<{ validateTwoFactorByToken: string }>(VALIDATE_TWO_FACTOR_BY_TOKEN);

  const handleTwoFactorBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTwoFactorBoxValue(e.target.value);
  };

  const handleRequestCode = async () => {
    if (!token) return;
    try {
      const { data } = await requestTwoFactorByToken({ variables: { token } });
      if (data?.requestTwoFactorByToken) {
        toast.success('Two-factor code sent to your email');
        setCodeRequested(true);
      }
    } catch (error) {
      toast.error('Failed to request code');
    }
  };

  const handleVerify = async () => {
    if (!token) return;
    setVerifying(true);
    try {
      const { data } = await validateTwoFactorByToken({ variables: { token, code: twoFactorBoxValue } });
      if (data?.validateTwoFactorByToken) {
        setTwofactorPass(true, data.validateTwoFactorByToken);
      } else {
        toast.error('Invalid two-factor code');
        setVerifying(false);
      }
    } catch (error) {
      toast.error('Verification failed');
      setVerifying(false);
    }
  };

  return (
    <div className="flex items-center justify-center pt-12">
      <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow">
        {/* Logo */}
        <div className="flex justify-center">
          <img src={`${process.env.PUBLIC_URL}/logoODR.png`} alt="logo" className="h-16 w-auto" />
        </div>

        {/* Title */}
        <h2 className="text-center text-2xl font-bold">Message Center</h2>
        <h3 className="text-center text-xl font-bold">Two-Factor Authentication</h3>

        {/* Request Button */}
        {!codeRequested && (
          <button
            onClick={handleRequestCode}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Request Code
          </button>
        )}

        {/* Verification Form */}
        {codeRequested && (
          <>
            <div className="text-center text-sm text-gray-600">
              A code has been sent to your email address
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Two-Factor Code</label>
              <input
                type="text"
                value={twoFactorBoxValue}
                onChange={handleTwoFactorBoxChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter code"
              />
            </div>
            <button
              onClick={handleVerify}
              disabled={verifying}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {verifying ? <Loader className="h-5 w-5 animate-spin" /> : 'Verify'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TwoFactorLogin;